/**
 * Avatar atom — circular display for user. Shows image when src provided;
 * falls back to initials on load error or when no src.
 * @see https://ui.shadcn.com/docs/components/radix/avatar
 */

import React, { useState, useEffect } from "react";
import type { AvatarProps } from "./Avatar.types";
import {
  getAvatarRootClasses,
  getAvatarInnerClasses,
  getAvatarFallbackClasses,
  getAvatarImageClasses,
} from "./Avatar.styles";
import { getAvatarInitials } from "./Avatar.utils";
import { cn } from "@/utils/cn";

export const Avatar: React.FC<AvatarProps> = ({
  initials,
  src,
  alt,
  size = "md",
  variant = "neutral",
  className,
  children,
}) => {
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    if (src) setImageError(false);
  }, [src]);

  const showImage = Boolean(src) && !imageError;
  const fallbackText = getAvatarInitials(initials);

  return (
    <div
      className={cn("group/avatar", getAvatarRootClasses(size, className))}
      data-size={size}
      role="img"
      aria-label={alt ?? initials}
      aria-hidden={!alt && !src}
    >
      <div className={getAvatarInnerClasses()}>
        {showImage ? (
          <img
            src={src}
            alt={alt ?? ""}
            className={getAvatarImageClasses()}
            onError={() => setImageError(true)}
            loading="lazy"
            decoding="async"
          />
        ) : (
          <span className={getAvatarFallbackClasses(size, variant)}>
            {fallbackText}
          </span>
        )}
      </div>
      {children}
    </div>
  );
};
